// What the phone asks the app to open, left by native code in the app's preferences
// (Capacitor Preferences, group "CapacitorStorage") until the web app takes it:
//   bb_open_url   a tapped notification or an app-icon shortcut (/app/...; with
//                 ?quick_add=1 the new-task form opens)
//   bb_share      something shared into the app from another app's share sheet, as JSON
//                 { title, text, files: [{ path, name, type }], target }: text, a link, photos
//                 or files (copied into the app's own storage), and where it goes:
//                   'new_task' (or none: the app itself was picked)  a new task with it
//                   'ask_busybee'                                     a new chat with BusyBee
//                   'bot_<id>'                                        a new chat with that bot
//                 Android: MainActivity.java (the share sheet's app and its Direct Share
//                 targets, ShareTargets.java); iOS: the share extension (ios/App/ShareExtension).
//   bb_share_bots the bots the share sheet offers, written here for the native side (shareBots)
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const take = async (key) => {
  const { value } = await Preferences.get({ key });
  if (value) await Preferences.remove({ key });
  return value;
};

/** A share as the native side left it, or null (nothing, or not readable). */
export const parseShare = (raw) => {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    const files = Array.isArray(s.files) ? s.files.filter((f) => f && f.path) : [];
    const text = typeof s.text === 'string' ? s.text : '';
    const title = typeof s.title === 'string' ? s.title : '';
    if (!text.trim() && !title.trim() && !files.length) return null;
    return { title, text, files, target: typeof s.target === 'string' ? s.target : null };
  } catch {
    return null;
  }
};

export async function takePending() {
  const url = await take('bb_open_url');
  const share = parseShare(await take('bb_share'));
  return { url, share };
}

/** Where a share goes: { kind: 'task' } or { kind: 'chat', botId } (null botId: BusyBee). */
export const shareDestination = (target) => {
  if (target === 'ask_busybee') return { kind: 'chat', botId: null };
  const bot = /^bot_(.+)$/.exec(target || '');
  if (bot) return { kind: 'chat', botId: bot[1] };
  return { kind: 'task' };
};

/** A shared text as a new task: a title (the subject, the first line, else the first file's
 * name) and the rest. */
export function taskFromShare({ title, text, files = [] }) {
  const body = (text || '').trim();
  const first = body.split('\n')[0].trim();
  const name = (title || '').trim() || first.slice(0, 200) || (files[0]?.name || '');
  const rest = title ? body : body.split('\n').slice(1).join('\n').trim();
  return { title: name, description: rest || (title ? body : '') };
}

/** The shared text as one message to a bot: the subject, then the text. */
export const messageFromShare = ({ title, text }) => [title, text].map((s) => (s || '').trim())
  .filter(Boolean).filter((s, i, all) => all.indexOf(s) === i).join('\n\n');

/** The shared files as File objects, read from the app's storage (those it cannot read are skipped). */
export async function filesFromShare(files = []) {
  const read = await Promise.all(files.map(async (f) => {
    try {
      const res = await fetch(Capacitor.convertFileSrc(f.path.startsWith('file://') ? f.path : `file://${f.path}`));
      if (!res.ok) return null;
      const blob = await res.blob();
      return new File([blob], f.name || 'file', { type: f.type || blob.type || 'application/octet-stream' });
    } catch {
      return null;
    }
  }));
  return read.filter(Boolean);
}

/**
 * The bots the phone's share sheet offers (Android Direct Share, iOS share suggestions and the
 * share extension's list): those of the latest chats first, then the rest, at most `max`.
 * Each { id, name, color } with the name as text shows it (botLabel).
 */
export const shareBots = (bots = [], recentBotIds = [], label = (b) => b.name, max = 4) => {
  const byId = new Map(bots.map((b) => [b.id, b]));
  const order = [...new Set([...recentBotIds.filter((id) => byId.has(id)), ...bots.map((b) => b.id)])];
  return order.slice(0, max).map((id) => {
    const b = byId.get(id);
    return { id: b.id, name: label(b), color: b.avatar_color || null };
  });
};

export const saveShareBots = (list) => Preferences.set({ key: 'bb_share_bots', value: JSON.stringify(list) });
